import { BaseApiClient } from './base-client';
import { type LoginCredentials, type RegisterData, type Token, type User, type UserSettings } from '../types/auth';

export class AuthApiClient extends BaseApiClient {
  async detectCountry(): Promise<{ country: string }> {
    return this.get<{ country: string }>('/api/v1/auth/detect-country');
  }

  async login(credentials: LoginCredentials): Promise<Token> {
    const params = new URLSearchParams();
    params.append('username', credentials.username);
    params.append('password', credentials.password);
    params.append('grant_type', 'password');

    const response = await this.axios.post<Token>('/api/v1/auth/login', params, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    });
    
    // Store tokens
    if (response.data.access_token) {
      await this.setAuthToken(response.data.access_token);
      if (response.data.refresh_token) {
        localStorage.setItem('refresh_token', response.data.refresh_token);
      }
    }

    return response.data;
  }

  async register(data: RegisterData): Promise<{ user: User; token: Token }> {
    try {
      const response = await this.post<{ user: User; token: Token }>('/api/v1/auth/register', data);
      
      // Ensure required fields are present
      const user: User = {
        ...response.user,
        login_count: response.user.login_count || 0,
        roles: response.user.roles || []
      };
      
      const result = {
        user,
        token: response.token
      };
      
      // Store tokens after successful registration
      if (response.token.access_token) {
        await this.setAuthToken(response.token.access_token);
        if (response.token.refresh_token) {
          localStorage.setItem('refresh_token', response.token.refresh_token);
        }
      }
      
      return result;
    } catch (error: any) {
      throw error;
    }
  }

  async getCurrentUser(): Promise<User> {
    const response = await this.get<User>('/api/v1/users/me');
    return {
      ...response,
      login_count: response.login_count || 0,
      roles: response.roles || []
    };
  }

  async updateCurrentUser(data: Partial<User>): Promise<User> {
    const response = await this.put<User>('/api/v1/users/me', data);
    return {
      ...response,
      login_count: response.login_count || 0,
      roles: response.roles || []
    };
  }

  async getUserSettings(): Promise<UserSettings> {
    return this.get<UserSettings>('/api/v1/settings/');
  }

  async updateUserSettings(settings: Partial<UserSettings>): Promise<UserSettings> {
    return this.put<UserSettings>('/api/v1/settings/', settings);
  }

  async checkPendingPlan(planData: any): Promise<{ status: string; message: string; order_id?: number; payment_intent?: any; redirect_url?: string }> {
    return this.post<{ status: string; message: string; order_id?: number; payment_intent?: any; redirect_url?: string }>('/api/v1/auth/check-pending-plan', planData);
  }

  async logout(): Promise<void> {
    await this.clearAuthToken();
    window.location.href = '/auth/login';
  }

  async requestPasswordReset(email: string): Promise<{ message: string }> {
    return this.post<{ message: string }>('/api/v1/auth/request-password-reset', { email });
  }

  async resetPassword(token: string, password: string): Promise<Token> {
    const response = await this.post<Token>('/api/v1/auth/reset-password', { token, password });
    
    // Store tokens
    if (response.access_token) {
      await this.setAuthToken(response.access_token);
      if (response.refresh_token) {
        localStorage.setItem('refresh_token', response.refresh_token);
      }
    }

    return response;
  }

  async convertToBusinessAccount(businessDetails: {
    name: string;
    code: string;
    vatNumber?: string;
    address: string;
    type: string;
  }): Promise<User> {
    const response = await this.post<User>('/api/v1/users/me/convert-to-business', {
      business_details: businessDetails
    });
    return {
      ...response,
      login_count: response.login_count || 0,
      roles: response.roles || []
    };
  }
}
